import { Auth0ContextInterface, useAuth0, User } from '@auth0/auth0-react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { createContext, useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { PaymentProvider } from '@/state/user-settings/types';
import { checkProductMatch, PlanGroup, ProductType } from '@d-id/types';

import { crossSiteDomain } from '@/utils/utils';

import { nanoid } from 'nanoid';
import { useAnalytics, useIdentifyUser } from '../analytics';
import { logger } from '../logger';
export { Auth0Provider } from '@auth0/auth0-react';
export interface DidUser extends User {
    features: string[];
    org_id?: string;
    owner_id?: string;
    productId?: string;
    isNew?: boolean;
    productName?: ProductType;
    planGroup?: PlanGroup;
    billingInterval?: 'month' | 'year';
    isAnonymous?: boolean;
    isTrial?: boolean;
    createdAt?: Date;
    priceId?: string;
    priceCredits?: string;
    isPrimary?: boolean;
    hashKey?: string;
    paymentProvider?: PaymentProvider;
    stripeCustomerId?: string;
    stripeSubscriptionId?: string;
    appsVisited?: string[];
    unlimitedExpiry?: Date;
}

const guestSubFromCookies = Cookies.get('guest_sub');

export const ANONYMOUS_USER: DidUser = {
    isAnonymous: true,
    features: [],
    email_verified: true,
    productName: ProductType.GUEST,
    planGroup: PlanGroup.GUEST,
    sub: guestSubFromCookies ?? uuidv4(),
};

export type AuthContext = Omit<Auth0ContextInterface<DidUser>, 'user' | 'getAccessTokenSilently'> & {
    accessToken: string;
    isAuthenticated: boolean;
    getAccessTokenSilently: (ignoreCache?: boolean) => Promise<string>;
    expandUserLocally: (localUser: Partial<DidUser>) => void;
    isAnonymous?: boolean;
    isOrg?: boolean;
    passwordReset?: boolean;
    resetPassword: () => Promise<string>;
    user: undefined | null | DidUser;
    setUser: (value: React.SetStateAction<DidUser | null>) => void;
};

interface Props {
    authDomain: string;
    audience: string;
    namespace: string;
    clientId: string;
    children: React.ReactNode;
}

const Store = createContext<AuthContext>({ isAnonymous: true } as AuthContext);
export const useAuth = () => useContext(Store);

export const AuthProvider = ({ authDomain, audience, namespace, clientId, children }: Props) => {
    const [accessToken, setAccessToken] = useState('');
    const [user, setUser] = useState<DidUser | null>(null);
    const [passwordReset, setPasswordReset] = useState(false);
    const { isAuthenticated, getAccessTokenSilently, loginWithRedirect, user: authUser, ...authProps } = useAuth0();
    const mixpanel = useAnalytics();
    const identifyUser = useIdentifyUser();

    const _getAccessTokenSilently = async (ignoreCache: boolean = false) => {
        let hashKey = Cookies.get('hash_key');

        if (!hashKey) {
            hashKey = nanoid();
            Cookies.set('hash_key', hashKey, { domain: crossSiteDomain });
        }

        return await getAccessTokenSilently({
            audience: `https://${audience}/api/v2/`,
            scope: 'read:current_user',
            ignoreCache,
            hash_key: hashKey,
        });
    };
    const _loginWithRedirect = async (options: any) => {
        let hashKey = Cookies.get('hash_key');

        if (!hashKey) {
            hashKey = nanoid();
            Cookies.set('hash_key', hashKey, { domain: crossSiteDomain });
        }
        await loginWithRedirect({ ...options, hash_key: hashKey });
    };

    const getAndSetToken = (localParams?: Partial<DidUser>) => {
        _getAccessTokenSilently()
            .then(token => {
                setAccessToken(token);
            })
            .catch(() => {
                setAccessToken('');
            });
        const {
            [`${namespace}/features`]: strFeatures = '',
            [`${namespace}/stripe_product_id`]: productId,
            [`${namespace}/stripe_product_name`]: productName,
            [`${namespace}/stripe_plan_group`]: planGroup,
            [`${namespace}/stripe_billing_interval`]: billingInterval = 'month',
            [`${namespace}/stripe_price_id`]: priceId,
            [`${namespace}/stripe_price_credits`]: priceCredits,
            [`${namespace}/stripe_customer_id`]: stripeCustomerId,
            [`${namespace}/stripe_subscription_id`]: stripeSubscriptionId,
            [`${namespace}/provider`]: provider,
            [`${namespace}/is_new`]: isNew,
            [`${namespace}/org_id`]: org_id,
            [`${namespace}/creation_timestamp`]: createdAt,
            [`${namespace}/hash_key`]: hashKey,
            [`${namespace}/primary`]: isPrimary,
            [`${namespace}/payment_provider`]: paymentProvider,
            [`${namespace}/apps_visited`]: appsVisited,
            [`${namespace}/unlimited_expiry`]: unlimitedExpiry,
            ...rest
        } = authUser!;

        if (!planGroup) {
            //TODO: Throw error after migration period 2023-01-30
            logger.warn('No plan group found in user', { user: authUser?.sub });
        }
        const features = strFeatures.split(',');

        setUser({
            isAnonymous: false,
            appsVisited,
            features,
            productName,
            productId,
            ...(!checkProductMatch.TRIAL(planGroup ?? productName) && { billingInterval }),
            priceId,
            priceCredits,
            stripeCustomerId,
            stripeSubscriptionId,
            planGroup,
            provider,
            isNew,
            isTrial: checkProductMatch.TRIAL(planGroup ?? productName),
            org_id,
            ...rest,
            ...localParams,
            createdAt: new Date(createdAt),
            isPrimary,
            paymentProvider,
            hashKey,
            unlimitedExpiry,
        });
    };

    const expandUserLocally = (localUser: Partial<DidUser>) => {
        !user?.isAnonymous && setUser({ features: [], ...user, ...localUser });
    };

    const resetPassword = async (): Promise<string> => {
        setPasswordReset(true);
        const { data } = await axios.post(
            `https://${authDomain}/dbconnections/change_password`,
            {
                client_id: clientId,
                email: user?.email,
                connection: 'Username-Password-Authentication',
            },
            {
                headers: { 'content-type': 'application/json' },
            }
        );
        return data;
    };

    useEffect(() => {
        if (isAuthenticated && user) {
            const { sub, planGroup, hashKey } = user;
            Cookies.set('did_user_info', JSON.stringify({ sub, planGroup }), { domain: crossSiteDomain });
            const cookiesHashKey = Cookies.get('hash_key');

            if (hashKey && hashKey !== cookiesHashKey) {
                Cookies.set('hash_key', hashKey, { domain: crossSiteDomain });
            }
        }
    }, [user, isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated) {
            getAndSetToken();
        } else if (!authProps.isLoading) {
            setUser(ANONYMOUS_USER);
            Cookies.set('guest_sub', ANONYMOUS_USER.sub!, {
                domain: crossSiteDomain,
            });
            setAccessToken('');
        }
    }, [isAuthenticated, authProps.isLoading, authUser]);

    useEffect(() => {
        if (mixpanel && user) {
            identifyUser(user);
            mixpanel.enrich({
                currentPlan: user.planGroup,
                currentBilling: user.billingInterval,
                isAnonymous: user.isAnonymous,
                currentPlanCredits: user.priceCredits,
            });
        }
    }, [mixpanel, user]);

    const authRes = {
        accessToken,
        isAuthenticated,
        getAccessTokenSilently: _getAccessTokenSilently,
        passwordReset,
        expandUserLocally,
        isAnonymous: user?.isAnonymous,
        isOrg: !!(user?.org_id && user.org_id !== ''),
        resetPassword,
        user,
        setUser,
        loginWithRedirect: _loginWithRedirect,
        ...authProps,
    };

    return <Store.Provider value={authRes}>{children}</Store.Provider>;
};

export function isStripePayingUser(user: DidUser | null | undefined) {
    if (!user) {
        return false;
    }
    return (
        !user.isAnonymous &&
        user.planGroup !== PlanGroup.TRIAL &&
        user.planGroup !== PlanGroup.FREE &&
        user.stripeCustomerId &&
        user.stripeSubscriptionId
    );
}
